import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { request } from '@apis/utils';
import { postInterest } from '@apis/interest';
import { validateEmail, identifyHubspotUser } from '@helpers/forms';
import { sendEvent } from '@lib/gtm.js';
import { validatePhone } from '@hiredigital/lib/helpers/utils';

import Button from '@hiredigital/ui/Button';
import TextInput from '@hiredigital/ui/Input/SimpleTextInput';
import TextArea from '@hiredigital/ui/Input/TextArea';
import Select from '@hiredigital/ui/Input/Select';

import { submitHubspotForm } from '@components/Registration/common/helpers';

import Styles from './EnquiryForm.module.scss';

const EnquiryForm = (props) => {
  const router = useRouter();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [country, setCountry] = useState(null);
  const [countries, setCountryList] = useState([]);
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadCountryList();
  }, []);

  useEffect(() => {
    const { query } = router;
    if (!!Object.keys(query).length) {
      setName(query.name);
      setEmail(query.email);
      setCompany(query.company);
      setMessage(query.message);
      setPhone(query.phone_number);
      setCountry(null);
    }
  }, [router.query]);

  const loadCountryList = async () => {
    const { data } = await request.get(`${process.env.NEXT_PUBLIC_SITE_URL}/data/countries.json`);
    const newCountryList =
      data.map(({ name }) => {
        return {
          value: name,
          label: name,
        };
      }) || [];
    setCountryList(newCountryList);
  };

  const callMethod = (name, value) => {
    const alias = {
      name: setName,
      email: setEmail,
      company: setCompany,
      phone: setPhone,
      message: setMessage,
      country: setCountry,
    };
    alias[name](value);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    callMethod(name, value);
    setEmailValid(true);
    setPhoneValid(true);
  };

  const handleEmailBlur = (event) => {
    setEmailValid(validateEmail(email));
  };

  const handleCountryUpdate = (obj) => {
    if (!obj || obj?.action === 'clear' || obj?.action === 'remove-value') {
      setCountry(null);
    } else {
      setCountry(obj);
    }
  };

  const handleSubmit = async () => {
    sendEvent({ event: 'GTM-ContactUs' });
    setSubmitted(true);

    const isPhoneValid = validatePhone(phone);
    const isEmailValid = validateEmail(email);
    setPhoneValid(isPhoneValid);
    setEmailValid(isEmailValid);

    if (isEmailValid && name?.length > 0 && isPhoneValid && message?.length > 0) {
      setLoading(true);
      const pageUrl = window.location.href;
      const pageName = document.title;

      const data = {
        name,
        email,
        message,
        phone,
        company: company || '',
        country: country ? country.value : '',
        form_type: 'General Enquiry',
        pageUrl,
        pageName,
      };

      // Identify User Block
      try {
        identifyHubspotUser(data);
        const hubspotData = {
          ...router.query,
          email,
          message,
          phone,
          company: company || '',
          country: country ? country.value : '',
          form_type: 'General Enquiry',
          pageUrl,
          pageName,
          fullName: name,
          workEmail: email,
          site: 'Hire Digital',
        };
        await submitHubspotForm(
          process.env.NEXT_PUBLIC_CONTACT_FORMID,
          pageUrl,
          pageName,
          hubspotData
        );

        sendEvent({ event: 'GTM-ContactUsConfirm' });
      } catch (err) {
        console.error(err);
      }

      await postInterest(data)
        .then((response) => {
          if (response.data) {
            setMessageSent(true);
            setName('');
            setEmail('');
            setMessage('');
            setPhone('');
            setCompany('');
            setCountry(null);
            setSubmitted(false);

            setTimeout(() => {
              setLoading(false);
              setMessageSent(false);
            }, 5000); // wait 5 seconds, then reset to false
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  let successMessage, submitBtn;
  if (messageSent) {
    successMessage = <div className={Styles.successMessage}>{`Your message has been sent.`}</div>;
  } else if (loading) {
    submitBtn = (
      <Button type={Button.Type.DARKBLUE} className={Styles.contactButton}>
        <div className={Styles.loader}>
          <div className={Styles.dot} />
          <div className={Styles.dot} />
          <div className={Styles.dot} />
        </div>
      </Button>
    );
  } else {
    submitBtn = (
      <Button type={Button.Type.DARKBLUE} className={Styles.contactButton} onClick={handleSubmit}>
        {`Submit`}
        <img
          height='1'
          width='1'
          style={{ display: 'none' }}
          alt=''
          src={process.env.NEXT_PUBLIC_LI_CONTACT}
        />
      </Button>
    );
  }

  const checkEmail = () => {
    if (submitted && !email) {
      return 'Enter your email address';
    } else if (email) {
      if (!emailValid) {
        return 'Enter a valid email address';
      }
    }
  };

  const validatePhoneNumber = () => {
    if (phone && !phoneValid) {
      return 'Enter a valid phone number';
    }
  };

  return (
    <div className={Styles.panel}>
      <div className={Styles.wrapper}>
        <div className={Styles.formSection}>
          <div className={Styles.inputContainer}>
            <TextInput
              name='name'
              label='Name*'
              error={submitted && !name && 'Enter your full name'}
              value={name}
              onChange={handleChange}
            />
          </div>
          <div className={Styles.inputGroup}>
            <div className={Styles.inputContainer}>
              <TextInput
                name='email'
                label='Work Email*'
                error={checkEmail()}
                value={email}
                onBlur={handleEmailBlur}
                onChange={handleChange}
              />
            </div>

            <div className={Styles.inputContainer}>
              <TextInput
                name='company'
                label='Company Name'
                value={company}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className={Styles.inputGroup}>
            <div className={Styles.inputContainer}>
              <TextInput
                name='phone'
                label='Phone Number'
                error={validatePhoneNumber()}
                value={phone}
                onChange={handleChange}
              />
            </div>
            <div className={Styles.inputContainer}>
              <Select
                className={Styles.select}
                value={country}
                classNamePrefix='s-contact'
                name='country'
                label='Country'
                isClearable
                options={countries}
                onChange={handleCountryUpdate}
              />
            </div>
          </div>
          <div className={Styles.inputContainer}>
            <TextArea
              name='message'
              error={submitted && !message && 'Enter a message'}
              label='Let us know more about your enquiry*'
              value={message}
              onChange={handleChange}
            />
          </div>
          <div className={Styles.actionContainer}>
            {successMessage}

            <div className={Styles.actions}>{submitBtn}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryForm;
